import './AttachPickerManager.sass';

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import LinkifyIt from 'linkify-it';
import { roundToNearestMinutes } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import {
  AttachPickerManagerProps,
  AttachPickerManagerRef,
} from './AttachPickerManager.interface';
import {
  attachActions,
  attachPhotosSelector,
  attachPollSelector,
  attachRouletteSelector,
  attachSelector,
  attachTimerSelector,
} from '../../../store/attach';
import { RootState } from '$store/rootReducer';
import Photos from '$shared/components/AttachPickerManager/Photos';
import { classNames } from '$utils';
import Timer from './Timer';
import Link from './Link';
import { getImageSize } from '$utils/image';
import { toastActions } from '$store/toast';
import { useTranslator } from '$hooks';
import { showModal } from '$navigation/helper';
import { Poll } from '$shared/components/AttachPickerManager/Poll';
import { Roulette } from '$shared/components/AttachPickerManager/Roulette';

const AttachPickerManager = forwardRef<AttachPickerManagerRef, AttachPickerManagerProps>(
  (props, ref) => {
    const { pickerId, mini = false, asPeerId = 0 } = props;

    const photos = useSelector((state: RootState) =>
      attachPhotosSelector(state, pickerId),
    );
    const timer = useSelector((state: RootState) => attachTimerSelector(state, pickerId));
    const poll = useSelector((state: RootState) => attachPollSelector(state, pickerId));
    const roulette = useSelector((state: RootState) =>
      attachRouletteSelector(state, pickerId),
    );
    const linkTimer = useRef<any>(0);
    const t = useTranslator();
    const { linkFailed } = useSelector(attachSelector);

    const dispatch = useDispatch();
    const linkify = useMemo(() => {
      return LinkifyIt();
    }, []);

    useEffect(() => {
      if (photos.length && props.onChange) {
        props.onChange();
      }
    }, [photos]);

    useEffect(() => {
      return () => {
        clearTimeout(linkTimer.current);
        //dispatch(attachActions.clear(pickerId));
      };
    }, [dispatch, pickerId]);

    useImperativeHandle(ref, () => ({
      async uploadImages(files) {
        const validFiles: File[] = [];
        for (const file of Array.from(files)) {
          const { width, height } = await getImageSize(file);
          if (height <= width * 3) {
            validFiles.push(file);
          }
        }

        if (files.length !== validFiles.length) {
          dispatch(toastActions.setToastFail(t('photo_validation_size_err')));
        }

        dispatch(
          attachActions.uploadPhotos({
            pickerId,
            files: validFiles,
            watermark: props.watermark,
            asPeerId,
          }),
        );
      },
      uploadVideo(file: File) {
        dispatch(
          attachActions.uploadVideo({
            pickerId,
            file,
            dispatch,
            asPeerId,
          }),
        );
        dispatch(attachActions.setIsAdditionalModalOpened(true));
        showModal('upload_video');
      },
      findLinks(text: string) {
        clearTimeout(linkTimer.current);

        const links = linkify.match(text);
        if (links) {
          for (let link of links) {
            let url;
            try {
              url = new URL(link.raw);
            } catch (e) {
              continue;
            }
            if (
              [
                'ton.place',
                'localhost',
                'youtu.be',
                'youtube.com',
                'www.youtube.com',
                'getgems.io',
                'ton.diamonds',
              ].indexOf(url.hostname) > -1
            ) {
              const link = links[0].raw;
              if (linkFailed[link]) {
                continue;
              }

              linkTimer.current = setTimeout(() => {
                dispatch(
                  attachActions.loadLink({
                    pickerId,
                    link,
                  }),
                );
              }, 1000);
              break;
            }
          }
        }
      },
      toggleTimer() {
        if (timer) {
          dispatch(
            attachActions.setTimer({
              pickerId,
              time: null,
            }),
          );
        } else {
          let time = Date.now() + 3600 * 1000;
          time = roundToNearestMinutes(time, {
            nearestTo: 5,
          }).getTime();

          dispatch(
            attachActions.setTimer({
              pickerId,
              time,
            }),
          );
        }
      },
    }));

    const handleClick = (e) => {
      const target = e.target;

      const hasControlsClass = target.closest('[class*="__controls"]');
      if (hasControlsClass) {
        return;
      }

      if (target.closest('.Collage__item')) {
        e.stopPropagation();

        const el = target.closest('.Collage__item');
        const id = el.dataset.id * (el.classList.contains('video') ? -1 : 1);
        showModal(`photos+admin+${id}`);
        return;
      }
    };

    return (
      <div
        className={classNames({
          AttachPickerManager: true,
          hidden: props.hidden,
          mini,
        })}
        onClick={handleClick}
      >
        {poll > 0 && <Poll pickerId={pickerId} />}
        {roulette > 0 && <Roulette pickerId={pickerId} />}
        <Timer pickerId={pickerId} />
        <Photos pickerId={pickerId} photos={photos} mini={mini} />
        <Link pickerId={pickerId} mini={mini} />
      </div>
    );
  },
);

AttachPickerManager.displayName = 'AttachPickerManager';

export default AttachPickerManager;
